import * as React from 'react';

import { toMoney } from '@rematter/common/money';
import { enumPaymentStatus } from '@rematter/frontend-lib-api-client';
import {
  Button,
  FooterItem,
  humanReadableNumber,
  Money as MoneyField,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import {
  $currentDraftPaymentInfo,
  $discardAllDrafts,
  $payment,
  $paymentId,
  $remotePaymentDetail,
  $updatePayment,
} from '../../_state/paymentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { toMoneyInput } from '~/modules/trade/utils/money';
import { getLocale } from '~/shared/utils/locale';

//
// ⚛️ ⚛️ ⚛️ PAYMENT DETAIL FOOTER ⚛️ ⚛️ ⚛️
//
export const PaymentDetailFooter = () => {
  // Translations
  const t = useTranslations();
  const toast = useToast();

  // Atoms
  const paymentId = useAtomValue($paymentId);
  const payment = useAtomValue($payment);
  const queryResult = useAtomValue($remotePaymentDetail);
  const refetch = queryResult?.refetch;
  const draftPaymentInfo = useAtomValue($currentDraftPaymentInfo);
  const updatePayment = useSetAtom($updatePayment);
  const discardAllDrafts = useSetAtom($discardAllDrafts);

  // State
  const [isSaving, setIsSaving] = React.useState(false);

  // Memoized
  const isDisabled = React.useMemo(
    () => payment?.status === enumPaymentStatus.Voided,
    [payment?.status],
  );

  const totalApplied = React.useMemo(() => {
    if (!payment?.amount || !payment?.unappliedAmount) return null;
    return toMoney({
      amount: payment.amount.amount - payment.unappliedAmount.amount,
      currencyCode: payment.amount.currencyCode,
    });
  }, [payment?.amount, payment?.unappliedAmount]);

  // Callbacks
  const handleSave = React.useCallback(async () => {
    if (!paymentId || isSaving) return;

    setIsSaving(true);
    try {
      await updatePayment({
        id: paymentId,
        documentNumber:
          draftPaymentInfo?.documentNumber ?? payment?.documentNumber,
        transactionDate:
          draftPaymentInfo?.transactionDate ?? payment?.transactionDate,
        amount: draftPaymentInfo?.amount
          ? toMoneyInput(draftPaymentInfo.amount)
          : payment?.amount
            ? toMoneyInput(payment.amount)
            : undefined,
        paymentMethod:
          draftPaymentInfo?.paymentMethod ?? payment?.paymentMethod,
        checkNumber: draftPaymentInfo?.checkNumber ?? payment?.checkNumber,
        checkMemo: draftPaymentInfo?.checkMemo ?? payment?.checkMemo,
        achNumber: draftPaymentInfo?.achNumber ?? payment?.achNumber,
        wireNumber: draftPaymentInfo?.wireNumber ?? payment?.wireNumber,
      });

      toast.success(t.payments.detail.messages.updateSuccess);
      refetch?.();
    } catch (err) {
      toast.error(
        t.payments.detail.messages.updateError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    paymentId,
    isSaving,
    draftPaymentInfo,
    payment,
    updatePayment,
    toast,
    refetch,
    t,
  ]);

  const handleDiscard = React.useCallback(() => {
    discardAllDrafts();
  }, [discardAllDrafts]);

  if (!payment) return null;

  return (
    <Row xEnd yCenter gap5 className="@PaymentDetailFooter">
      <Row xEnd yCenter gap8 className="[&>*]:min-h-auto">
        <FooterItem label={t.payments.detail.footer.totalAmount}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...payment.amount}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.payments.detail.footer.totalApplied}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...totalApplied}
            locale={getLocale()}
          />
        </FooterItem>
        <FooterItem label={t.payments.detail.footer.linkedDocuments}>
          <span className="text-lg leading-none font-bold">
            {humanReadableNumber(
              (payment.linkedBills?.length ?? 0) +
                (payment.linkedInvoices?.length ?? 0),
              { locale: getLocale(), decimalPlaces: 0 },
            )}
          </span>
        </FooterItem>
        <FooterItem label={t.payments.detail.footer.unappliedAmount}>
          <MoneyField
            className="text-lg leading-none font-bold"
            {...payment.unappliedAmount}
            locale={getLocale()}
          />
        </FooterItem>
      </Row>
      {!isDisabled && (
        <Row yCenter gap3 className="flex-shrink-0">
          <Button
            color="neutral"
            variant="outline"
            onPress={handleDiscard}
            isDisabled={isSaving}
          >
            {t.payments.detail.unsavedChanges.discard}
          </Button>
          <Button
            color="primary"
            variant="solid"
            onPress={handleSave}
            isPending={isSaving}
            isLoading={isSaving}
          >
            {t.payments.detail.unsavedChanges.save}
          </Button>
        </Row>
      )}
    </Row>
  );
};
